import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../lib/session";
import { useParish } from "../lib/parishContext";
import { useFeatureFlag } from "../lib/useFeatureFlag";
import { useIsNarrow } from "../lib/useIsNarrow";
import { supabase } from "../lib/supabaseClient";
import { resolveApiUrl } from "../lib/url";
import SacristyLogo from "./SacristyLogo";

interface DashboardHeaderProps {
  // Optional content slotted between the chrome row (logo + parish
  // chip) and the tab-actions row. The dashboard greeting + summary
  // lives here; other consumers (HolyWeek, settings, the skeleton)
  // pass nothing and the slot collapses.
  greeting?: ReactNode;
}

export default function DashboardHeader({ greeting }: DashboardHeaderProps = {}) {
  const session = useSession();
  const navigate = useNavigate();
  const holyWeekEnabled = useFeatureFlag("holyWeek");
  const isNarrow = useIsNarrow();
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  // Email templates and Volunteers are Normal-Schedule artifacts;
  // suppress them when the Holy Week tab is active.
  const onHolyWeek = location.pathname.startsWith("/holy-week");
  // On phones the Email templates / Volunteers pair lives in the
  // fixed BottomTabBar instead of the header. Print roster (Holy
  // Week only) stays in the header since it's surface-specific.
  const hideNormalActionsInHeader = isNarrow && !onHolyWeek;

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const goSettings = () => {
    setMenuOpen(false);
    navigate("/settings");
  };

  const signOut = async () => {
    setMenuOpen(false);
    if (supabase) {
      await supabase.auth.signOut();
    }
    navigate("/sign-in");
  };

  return (
    <header className="dashboard-header">
      <div className="dashboard-header__top">
        <Link to="/" className="dashboard-header__brand" aria-label="Sacristy AI home">
          <SacristyLogo />
        </Link>
        <div className="dashboard-header__right" ref={wrapperRef}>
          <ParishChip />
          <button
            type="button"
            className="dashboard-header__account"
            aria-label="Account menu"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <AccountIcon />
          </button>
          {menuOpen ? (
            <div className="dashboard-header__menu" role="menu">
              <p className="dashboard-header__menu-name">{session.displayName}</p>
              <p className="dashboard-header__menu-email">{session.email}</p>
              <div className="dashboard-header__menu-divider" />
              <button
                type="button"
                className="dashboard-header__menu-item"
                onClick={goSettings}
                role="menuitem"
              >
                Settings
              </button>
              <button
                type="button"
                className="dashboard-header__menu-item"
                onClick={signOut}
                role="menuitem"
              >
                Sign out
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {(() => {
        const tabActions = hideNormalActionsInHeader ? null : (
          <div className="dashboard-header__tab-actions">
            {onHolyWeek ? (
              <a
                href="/holy-week/print"
                target="_blank"
                rel="noopener"
                className="dashboard-header__tab-action"
              >
                <PrintIcon />
                <span>Print roster</span>
              </a>
            ) : (
              <>
                <Link
                  to="/templates"
                  state={{ backgroundLocation: location }}
                  className="dashboard-header__tab-action"
                >
                  <EnvelopeIcon />
                  <span>Email templates</span>
                </Link>
                <Link
                  to="/volunteers"
                  state={{ backgroundLocation: location }}
                  className="dashboard-header__tab-action"
                >
                  <VolunteersIcon />
                  <span>Volunteers</span>
                </Link>
              </>
            )}
          </div>
        );

        // When the greeting is provided and the Holy Week NavLinks
        // aren't in play, fold the action pills + bottom rule into
        // the greeting row so the chrome reads as one band.
        const inlineActions = !!greeting && !holyWeekEnabled && tabActions !== null;

        return (
          <>
            {greeting ? (
              <div
                className={`dashboard-header__greeting${
                  inlineActions ? " dashboard-header__greeting--with-actions" : ""
                }`}
              >
                <div className="dashboard-header__greeting-text">{greeting}</div>
                {inlineActions ? tabActions : null}
              </div>
            ) : null}

            {!inlineActions && (holyWeekEnabled || tabActions !== null) ? (
              <nav className="dashboard-header__tabs" aria-label="Primary">
                {holyWeekEnabled ? (
                  <>
                    <NavLink
                      to="/"
                      end
                      className={({ isActive }) =>
                        `dashboard-header__tab${isActive ? " is-active" : ""}`
                      }
                    >
                      Normal Schedule
                    </NavLink>
                    <NavLink
                      to="/holy-week"
                      className={({ isActive }) =>
                        `dashboard-header__tab${isActive ? " is-active" : ""}`
                      }
                    >
                      Holy Week
                    </NavLink>
                  </>
                ) : null}
                {tabActions}
              </nav>
            ) : null}
          </>
        );
      })()}
    </header>
  );
}

function VolunteersIcon() {
  return (
    <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
      <circle cx="6.5" cy="6.5" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M2 14.5 c0 -2.6 2 -4 4.5 -4 c2.5 0 4.5 1.4 4.5 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx="13" cy="7" r="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M10.5 14.5 c0.2 -2.1 1.6 -3.2 3 -3.2 c1.5 0 2.7 1 2.7 3.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PrintIcon() {
  return (
    <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
      <rect x="4.5" y="2.5" width="9" height="4" rx="0.4" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <rect x="2.5" y="6.5" width="13" height="6" rx="1" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <rect x="4.5" y="10" width="9" height="5.5" rx="0.4" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <line x1="6" y1="12" x2="12" y2="12" stroke="currentColor" strokeWidth="1.1" />
      <line x1="6" y1="13.7" x2="12" y2="13.7" stroke="currentColor" strokeWidth="1.1" />
    </svg>
  );
}

function EnvelopeIcon() {
  return (
    <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
      <rect
        x="2"
        y="4"
        width="14"
        height="10"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M2.5 5 L9 10 L15.5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Exported so VolunteerHeader (the volunteer portal's lean equivalent
// of this header) can reuse the same icon + chip instead of forking
// them.
export function AccountIcon() {
  return (
    <svg viewBox="0 0 16 16" width="18" height="18" aria-hidden="true">
      <circle cx="8" cy="6" r="2.6" fill="currentColor" />
      <path d="M2.7 14 a5.3 5.3 0 0 1 10.6 0 z" fill="currentColor" />
    </svg>
  );
}

// Small "you are in" indicator sitting immediately left of the
// account avatar. Reads as ambient context rather than a control —
// no tap target wired up; it's visual confirmation of which parish
// the signed-in scheduler is operating on. With a parish logo
// uploaded, a 20px thumbnail prefixes the display name. Without
// one, the display name stands alone.
export function ParishChip() {
  const { parish } = useParish();
  const logoSrc = resolveApiUrl(parish.logo_url);
  return (
    <div
      className="parish-chip"
      role="status"
      aria-label={`Active parish: ${parish.display_name}`}
    >
      {logoSrc ? (
        <img className="parish-chip__img" src={logoSrc} alt="" />
      ) : null}
      <span className="parish-chip__name">{parish.display_name}</span>
    </div>
  );
}
